/**
 * validation.ts
 * 
 * This utility provides standardized validation functions for various
 * input types across the application. It helps ensure data integrity
 * and security by validating user inputs before processing.
 */

/**
 * Validates an angle name for the custom angles service
 * @param angle - The angle name to validate
 * @returns {Object} Validation result with success status and message
 */
export const validateAngle = (angle: string): { isValid: boolean; message: string } => {
  // Trim the input first
  const trimmedAngle = angle.trim();
  
  // Check if angle is empty
  if (!trimmedAngle) {
    return { isValid: false, message: 'Angle name cannot be empty' };
  }
  
  // Check length (3-100 characters)
  if (trimmedAngle.length < 3) {
    return { isValid: false, message: 'Angle name must be at least 3 characters long' };
  }
  
  if (trimmedAngle.length > 100) {
    return { isValid: false, message: 'Angle name cannot exceed 100 characters' };
  }
  
  // Check for potentially harmful characters (scripts, etc.)
  const harmfulPattern = /<[^>]*script|javascript:|data:|vbscript:|file:|alert\s*\(|on\w+\s*=|<\/|<iframe|<object|<embed/i;
  if (harmfulPattern.test(trimmedAngle)) {
    return { isValid: false, message: 'Angle name contains invalid characters' };
  }
  
  return { isValid: true, message: 'Valid angle name' };
};

/**
 * Validates a custom ingredient name
 * @param name - The ingredient name to validate
 * @returns {Object} Validation result with success status and message
 */
export const validateIngredient = (name: string): { isValid: boolean; message: string } => {
  // Trim the input first
  const trimmedName = name.trim();
  
  // Check if name is empty
  if (!trimmedName) {
    return { isValid: false, message: 'Ingredient name cannot be empty' };
  }
  
  // Check length (3-100 characters)
  if (trimmedName.length < 3) {
    return { isValid: false, message: 'Ingredient name must be at least 3 characters long' };
  }
  
  if (trimmedName.length > 100) {
    return { isValid: false, message: 'Ingredient name cannot exceed 100 characters' };
  }
  
  // Check for potentially harmful characters
  const harmfulPattern = /<[^>]*script|javascript:|data:|vbscript:|file:|alert\s*\(|on\w+\s*=|<\/|<iframe|<object|<embed/i;
  if (harmfulPattern.test(trimmedName)) {
    return { isValid: false, message: 'Ingredient name contains invalid characters' };
  }
  
  return { isValid: true, message: 'Valid ingredient name' };
};

/**
 * Validates campaign notes for potential security issues
 * @param notes - The campaign notes to validate
 * @returns {Object} Validation result with success status and message
 */
export const validateCampaignNotes = (notes: string): { isValid: boolean; message: string } => {
  // Check for null or undefined
  if (notes === null || notes === undefined) {
    return { isValid: true, message: 'Empty notes are valid' };
  }
  
  // Check if length exceeds maximum
  const MAX_NOTES_LENGTH = 5000;
  if (notes.length > MAX_NOTES_LENGTH) {
    return { isValid: false, message: `Notes cannot exceed ${MAX_NOTES_LENGTH} characters` };
  }
  
  // Notes can contain various formatting, so we don't restrict content as strictly as names
  // We'll rely on sanitization before display instead
  
  return { isValid: true, message: 'Valid notes' };
};

/**
 * Validates a URL string
 * @param url - The URL to validate
 * @returns {Object} Validation result with success status and message
 */
export const validateUrl = (url: string): { isValid: boolean; message: string } => {
  if (!url || url.trim() === '') {
    return { isValid: false, message: 'URL cannot be empty' };
  }
  
  // Check for valid URL pattern
  try {
    const urlObj = new URL(url);
    const validProtocols = ['http:', 'https:'];
    
    if (!validProtocols.includes(urlObj.protocol)) {
      return { isValid: false, message: 'URL must use http or https protocol' };
    }
    
    return { isValid: true, message: 'Valid URL' };
  } catch (e) {
    return { isValid: false, message: 'Invalid URL format' };
  }
};

/**
 * Validates an email address format
 * @param email - The email address to validate
 * @returns {Object} Validation result with success status and message
 */
export const validateEmail = (email: string): { isValid: boolean; message: string } => {
  if (!email || email.trim() === '') {
    return { isValid: false, message: 'Email cannot be empty' };
  }
  
  // RFC 5322 compliant regex for email validation
  const emailPattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  if (!emailPattern.test(email)) {
    return { isValid: false, message: 'Invalid email format' };
  }
  
  return { isValid: true, message: 'Valid email' };
};

/**
 * General purpose text input validation with customizable rules
 * @param input - The text input to validate
 * @param options - Configuration options for validation
 * @returns {Object} Validation result with success status and message
 */
export const validateText = (
  input: string, 
  options: {
    minLength?: number;
    maxLength?: number;
    allowHtml?: boolean;
    allowedPattern?: RegExp;
    required?: boolean;
    fieldName?: string;
  } = {}
): { isValid: boolean; message: string } => {
  const {
    minLength = 0,
    maxLength = 1000,
    allowHtml = false,
    allowedPattern,
    required = true,
    fieldName = 'Input'
  } = options;
  
  // Handle null or undefined
  if (input === null || input === undefined) {
    input = '';
  }
  
  const trimmedInput = input.trim();
  
  // Check if required
  if (required && trimmedInput === '') {
    return { isValid: false, message: `${fieldName} is required` };
  }
  
  // If not required and empty, it's valid
  if (!required && trimmedInput === '') {
    return { isValid: true, message: 'Valid input' };
  }
  
  // Check length
  if (trimmedInput.length < minLength) {
    return { isValid: false, message: `${fieldName} must be at least ${minLength} characters` };
  }
  
  if (trimmedInput.length > maxLength) {
    return { isValid: false, message: `${fieldName} cannot exceed ${maxLength} characters` };
  }
  
  // Check for HTML if not allowed
  if (!allowHtml && /<[^>]+>/g.test(trimmedInput)) {
    return { isValid: false, message: `${fieldName} cannot contain HTML tags` };
  }
  
  // Check against custom pattern if provided
  if (allowedPattern && !allowedPattern.test(trimmedInput)) {
    return { isValid: false, message: `${fieldName} contains invalid characters or format` };
  }
  
  return { isValid: true, message: 'Valid input' };
}; 